import { state } from './state.js';
import * as API from './api.js';
import * as UI from './ui.js';
import * as ResultViewer from './result_viewer.js';
import { requestWorkflowScroll } from './workflow_selection.js';

const VIEWER_SOURCE_THUMB_SIZE = 800;

function finalizeHistoryViewerReadyState() {
    const resultCard = document.getElementById('resultCard');
    if (resultCard) {
        resultCard.classList.remove('is-loading');
        resultCard.classList.remove('compare-intro-mode');
    }
    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.add('hidden');
    const stopBtn = document.getElementById('stopBtn');
    if (stopBtn) stopBtn.style.display = 'none';
}

function resolveViewerSourceUrl(sourcePhotoId, fallbackUrl = '') {
    const safeSourceId = String(sourcePhotoId || '').trim();
    if (safeSourceId) {
        return `/source-thumb?source_id=${encodeURIComponent(safeSourceId)}&size=${VIEWER_SOURCE_THUMB_SIZE}`;
    }
    return String(fallbackUrl || '').trim();
}

function scrollResultIntoView(options = {}) {
    const pushHistory = options.pushHistory !== false;
    return new Promise((resolve) => {
        requestWorkflowScroll('viewer', {
            source: 'history_select',
            behavior: 'smooth',
            durationMs: 520,
            pushHistory,
            onDone: () => resolve(),
        });
    });
}

export async function handleHistorySelect(job, options = {}) {
    const safeJobId = String(job.job_id || '');
    state.jobId = safeJobId;
    state.selectedHistoryJobId = safeJobId;
    state.selectedHistoryJob = job;
    ResultViewer.prepareLoadingState(0, 0, { expandViewer: true });
    const scrollPromise = scrollResultIntoView(options);
    const ts = Number(job.timestamp || Date.now());
    const version = (Number.isFinite(ts) && ts > 0) ? Math.floor(ts) : Date.now();
    const base = `/result-image/${safeJobId}`;
    const previewDisplayUrl = `${base}?size=preview&v=${version}`;
    const inspectVariantUrl = `${base}?size=inspect&v=${version}`;
    const fallbackDownloadUrl = String(job.img_url || previewDisplayUrl).trim() || previewDisplayUrl;
    const manifestPromise = API.getManifestByKey(job.manifest_key, { includeMap: false });
    const sourceUrlFallback = job.source_url || '';
    const sourceDisplayUrl = resolveViewerSourceUrl(job.source_photo_id, sourceUrlFallback);

    try {
        await scrollPromise;
        if (String(state.selectedHistoryJobId || '') !== safeJobId) return;
        await ResultViewer.show(
            previewDisplayUrl,
            sourceDisplayUrl,
            null,
            fallbackDownloadUrl,
            inspectVariantUrl,
            safeJobId,
            { introHoldMs: 0, skipCompareIntro: true, skipSourceWait: true },
        );
        finalizeHistoryViewerReadyState();

        const manifest = await manifestPromise;
        if (String(state.selectedHistoryJobId || '') !== safeJobId) return;
        state.tileMap = Array.isArray(manifest.map) ? manifest.map : [];
        state.lastShownSettings = manifest.settings || null;
        const sourcePhotoId =
            job.source_photo_id ||
            (manifest.settings && manifest.settings.source_photo_id) ||
            null;
        const resolvedSourceDisplayUrl = resolveViewerSourceUrl(sourcePhotoId, sourceUrlFallback);
        const sourceUrl = sourceUrlFallback;
        const sourceKey = job.source_key || '';
        const canonicalSourceKey = job.canonical_source_key || sourceKey;
        let filename = '';
        if (sourceUrl) {
            const clean = String(sourceUrl).split('?')[0];
            const parts = clean.split('/');
            filename = parts[parts.length - 1] || '';
        }
        state.lastShownSourceInfo = {
            sourcePhotoId,
            sourceUrl,
            sourceKey,
            canonicalSourceKey,
            filename,
            jobId: job.job_id || '',
        };

        const makeUrl = (s3Key) => {
             if (!s3Key) return null;
             return `${base}?key=${encodeURIComponent(s3Key)}`;
        };

        let fullResKey = job.image_key || manifest.image_key;

        if (!fullResKey && job.manifest_key) {
            fullResKey = job.manifest_key.replace('.json', '.jpg');
        }

        const downloadUrl = makeUrl(fullResKey) || fallbackDownloadUrl;
        const inspectKey = manifest.inspect_key || '';
        const inspectUrl = makeUrl(inspectKey) || inspectVariantUrl;

        await ResultViewer.show(
            previewDisplayUrl,
            resolvedSourceDisplayUrl,
            manifest.settings || {},
            downloadUrl,
            inspectUrl,
            safeJobId,
            { introHoldMs: 0, skipImageFetch: true, skipCompareIntro: false, skipSourceWait: false },
        );
        finalizeHistoryViewerReadyState();
        UI.renderTrivia(manifest.trivia || null, manifest, {
            jobId: safeJobId,
            manifestKey: job.manifest_key || '',
        });

        if ((!Array.isArray(manifest.map) || manifest.map.length === 0) && job.manifest_key) {
            API.getManifestByKey(job.manifest_key, { includeMap: true })
                .then((fullManifest) => {
                    if (String(state.selectedHistoryJobId || '') !== safeJobId) return;
                    if (Array.isArray(fullManifest.map)) {
                        state.tileMap = fullManifest.map;
                    }
                })
                .catch(() => {});
        }

    } catch (e) {
        console.warn("Error loading history item manifest:", e);
        await scrollPromise.catch(() => {});
        if (String(state.selectedHistoryJobId || '') !== safeJobId) return;
        UI.renderTrivia(null);
        state.lastShownSettings = null;
        state.lastShownSourceInfo = {
            sourcePhotoId: job.source_photo_id || null,
            sourceUrl: sourceUrlFallback,
            sourceKey: job.source_key || '',
            canonicalSourceKey: job.canonical_source_key || job.source_key || '',
            filename: '',
            jobId: safeJobId,
        };
        state.selectedHistoryJob = job;
        state.tileMap = [];
        await ResultViewer.show(
            previewDisplayUrl,
            sourceDisplayUrl,
            null,
            fallbackDownloadUrl,
            inspectVariantUrl,
            safeJobId,
            { introHoldMs: 0, skipImageFetch: true, skipCompareIntro: false, skipSourceWait: false },
        );
        finalizeHistoryViewerReadyState();
        UI.renderTrivia(null);
    }
}
